export default function ContainerDocumentation() {
  const apiExamples = [
    {
      title: 'PDF 转图片',
      method: 'POST',
      endpoint: '/api/v1/pdf/to-image',
      description: '将 PDF 文档转换为图片格式',
      code: `curl -X POST https://api.example.com/v1/pdf/to-image \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -F "file=@document.pdf" \\
  -F "format=png" \\
  -F "dpi=300"`
    },
    {
      title: 'PDF 合并',
      method: 'POST',
      endpoint: '/api/v1/pdf/merge',
      description: '合并多个 PDF 文档为一个文件',
      code: `curl -X POST https://api.example.com/v1/pdf/merge \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -F "files[]=@doc1.pdf" \\
  -F "files[]=@doc2.pdf" \\
  -F "files[]=@doc3.pdf"`
    },
    {
      title: 'PDF 加水印',
      method: 'POST',
      endpoint: '/api/v1/pdf/watermark',
      description: '为 PDF 文档添加文字或图片水印',
      code: `curl -X POST https://api.example.com/v1/pdf/watermark \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -F "file=@document.pdf" \\
  -F "text=Confidential" \\
  -F "opacity=0.3"`
    }
  ];

  const quickStart = [
    {
      step: '1',
      title: '拉取镜像',
      command: 'docker pull documogul/pdf-rest-container:latest'
    },
    {
      step: '2',
      title: '启动容器',
      command: 'docker run -d -p 8080:8080 documogul/pdf-rest-container'
    },
    {
      step: '3',
      title: '测试接口',
      command: 'curl http://localhost:8080/api/v1/health'
    }
  ];

  return (
    <section id="documentation" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            快速开始
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            简单三步，即可开始使用 PDF REST Container
          </p>
        </div>

        {/* Quick Start Steps */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {quickStart.map((item, index) => (
            <div key={index} className="relative">
              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-8 border border-teal-100">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br from-teal-600 to-cyan-600 text-white text-2xl font-bold mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{item.title}</h3>
                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                  <code className="text-sm text-green-400 font-mono">
                    {item.command}
                  </code>
                </div>
              </div>
              {index < quickStart.length - 1 && (
                <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                  <i className="ri-arrow-right-line text-3xl text-teal-600"></i>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* API Examples */}
        <div>
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">API 示例</h3>
          <div className="space-y-8">
            {apiExamples.map((example, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border border-gray-200 shadow-lg">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="text-2xl font-bold text-gray-900 mb-2">{example.title}</h4>
                    <p className="text-gray-600">{example.description}</p>
                  </div>
                  <span className="px-4 py-1 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-full text-sm font-semibold whitespace-nowrap">
                    {example.method}
                  </span>
                </div>
                <div className="mb-4">
                  <code className="text-teal-600 font-mono text-sm">{example.endpoint}</code>
                </div>
                <div className="bg-gray-900 rounded-lg p-6 overflow-x-auto">
                  <pre className="text-sm text-green-400 font-mono">
                    {example.code}
                  </pre>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Documentation Links */}
        <div className="mt-16 grid md:grid-cols-3 gap-6">
          <a 
            href="#"
            className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-100 hover:shadow-lg transition-all cursor-pointer"
          >
            <i className="ri-book-open-line text-3xl text-teal-600 mb-3"></i>
            <h4 className="text-xl font-bold text-gray-900 mb-2">完整文档</h4>
            <p className="text-gray-600 text-sm">查看详细的 API 参考文档</p>
          </a>
          <a 
            href="#"
            className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-100 hover:shadow-lg transition-all cursor-pointer"
          >
            <i className="ri-code-box-line text-3xl text-teal-600 mb-3"></i>
            <h4 className="text-xl font-bold text-gray-900 mb-2">SDK 下载</h4>
            <p className="text-gray-600 text-sm">获取多语言 SDK 和示例代码</p>
          </a>
          <a 
            href="#"
            className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-100 hover:shadow-lg transition-all cursor-pointer"
          >
            <i className="ri-question-answer-line text-3xl text-teal-600 mb-3"></i>
            <h4 className="text-xl font-bold text-gray-900 mb-2">常见问题</h4>
            <p className="text-gray-600 text-sm">查找常见问题的解决方案</p>
          </a>
        </div>
      </div>
    </section>
  );
}
